/**
 * ObstructionFilterPills Component
 *
 * Active-filter chips for the obstruction sidebar. Each chip surfaces a slice
 * field via `FilterPillsList`; removing a chip dispatches the relevant
 * slice action (which also resets `page` per paginated-list Rule 1).
 */
import { memo, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import { useLocalizedDate } from '@/hooks';
import { useGeoMarkupStore } from '@/store';
import { FilterPillsList, type PillData } from '@/components/ui/FilterPill';
import type {
  ObstructionStatus,
  ObstructionType,
  SeverityLevel,
  TrafficImpact,
} from '@/types/obstructions';

export const ObstructionFilterPills = memo(function ObstructionFilterPills() {
  const { t } = useTranslation();
  const { formatDateCompact } = useLocalizedDate();

  const types = useGeoMarkupStore(
    useShallow((state) => state.obstructionFilters.types)
  );
  const statuses = useGeoMarkupStore(
    useShallow((state) => state.obstructionFilters.statuses)
  );
  const severities = useGeoMarkupStore(
    useShallow((state) => state.obstructionFilters.severities)
  );
  const impacts = useGeoMarkupStore(
    useShallow((state) => state.obstructionFilters.impacts)
  );
  const location = useGeoMarkupStore(
    (state) => state.obstructionFilters.location
  );
  const contractor = useGeoMarkupStore(
    (state) => state.obstructionFilters.contractor
  );
  const dateFrom = useGeoMarkupStore(
    (state) => state.obstructionFilters.dateFrom
  );
  const dateTo = useGeoMarkupStore((state) => state.obstructionFilters.dateTo);

  const setTypes = useGeoMarkupStore(
    (state) => state.obstructionFilters.setTypes
  );
  const setStatuses = useGeoMarkupStore(
    (state) => state.obstructionFilters.setStatuses
  );
  const setSeverities = useGeoMarkupStore(
    (state) => state.obstructionFilters.setSeverities
  );
  const setImpacts = useGeoMarkupStore(
    (state) => state.obstructionFilters.setImpacts
  );
  const setLocation = useGeoMarkupStore(
    (state) => state.obstructionFilters.setLocation
  );
  const setContractor = useGeoMarkupStore(
    (state) => state.obstructionFilters.setContractor
  );
  const setDateRange = useGeoMarkupStore(
    (state) => state.obstructionFilters.setDateRange
  );

  const pills = useMemo(() => {
    const result: PillData[] = [];

    if (types.length > 0) {
      const value = types
        .map((typeItem) => t(`obstructions.type.${typeItem}`))
        .join(', ');
      result.push({
        id: 'types',
        label: t('obstructions.filters.typeLabel'),
        value,
        onRemove: () => setTypes([] as ObstructionType[]),
      });
    }

    if (statuses.length > 0) {
      const value = statuses
        .map((statusItem) => t(`obstructions.status.${statusItem}`))
        .join(', ');
      result.push({
        id: 'statuses',
        label: t('obstructions.filters.statusLabel'),
        value,
        onRemove: () => setStatuses([] as ObstructionStatus[]),
      });
    }

    if (severities.length > 0) {
      const value = severities
        .map((severityItem) => t(`obstructions.severity.${severityItem}`))
        .join(', ');
      result.push({
        id: 'severities',
        label: t('obstructions.filters.severityLabel'),
        value,
        onRemove: () => setSeverities([] as SeverityLevel[]),
      });
    }

    if (impacts.length > 0) {
      const value = impacts
        .map((impactItem) => t(`obstructions.impact.${impactItem}`))
        .join(', ');
      result.push({
        id: 'impacts',
        label: t('obstructions.filters.impactLabel'),
        value,
        onRemove: () => setImpacts([] as TrafficImpact[]),
      });
    }

    if (location) {
      result.push({
        id: 'location',
        label: t('obstructions.filters.location'),
        value: location,
        onRemove: () => setLocation(''),
      });
    }

    if (contractor) {
      result.push({
        id: 'contractor',
        label: t('obstructions.filters.contractor'),
        value: contractor,
        onRemove: () => setContractor(''),
      });
    }

    if (dateFrom || dateTo) {
      const fromText = dateFrom
        ? formatDateCompact(dateFrom)
        : t('filters.none');
      const toText = dateTo ? formatDateCompact(dateTo) : t('filters.none');

      result.push({
        id: 'date-range',
        label: t('obstructions.filters.dateRange'),
        value: `${fromText} - ${toText}`,
        onRemove: () => setDateRange(null, null),
      });
    }

    return result;
  }, [
    types,
    statuses,
    severities,
    impacts,
    location,
    contractor,
    dateFrom,
    dateTo,
    t,
    formatDateCompact,
    setTypes,
    setStatuses,
    setSeverities,
    setImpacts,
    setLocation,
    setContractor,
    setDateRange,
  ]);

  return <FilterPillsList pills={pills} />;
});
